import { useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import LayoutContext from 'components/Layout/LayoutContext';
import Breadcrumb, {
  IBreadCrumbItem,
} from 'components/__ASSESSOR__/BreadCrumb/BreadCrumb';
import RelatoryCard from 'components/__ASSESSOR__/relatoryCard/relatoryCard';
import PrivateContainer from 'components/__SHARED__/PrivateContainer';
import { AuthContext } from 'context/authContext/AuthContext';
import { parseISO } from 'date-fns';
import { AssemblyModalities, SituationValues } from 'helpers/enums/Assembleia';
import { DateFormats } from 'helpers/enums/DateTypes';
import { getProfileByJWTRoles } from 'helpers/utils/AuthUtils';
import DownloadTrigger from 'helpers/utils/DownloadTrigger';
import { isNullOrUndefined } from 'helpers/utils/HTMLUtils';
import {
  getParsedJWTAssessorAccessToken,
  JWTAssessorJson,
} from 'helpers/utils/JWTUtils';
import { getAssembleiasAssessorRelatoriosEndpoint } from 'library/Apis';
import { Queries } from 'library/queries';
import { AssessorRoutePath } from 'library/Routes';
import translation from 'locales/translation';
import {
  HandleDownloadResponseParams,
  ReportStatus,
  ReportType,
  ReportTypes,
} from 'models/relatory.model';
import moment from 'moment';
import {
  Col,
  Collapse,
  Row,
  Title,
  ToastTypes,
  TopLoader,
} from 'plataforma-fundacao-componentes';
import {
  Suspense,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { Fragment } from 'react/jsx-runtime';
import {
  useGenerateReport,
  useGetPageReportInfo,
  useGetReports,
  useReportDownload,
} from 'services/relatory';
import {
  AuthUserPerfilId,
  IAuthUserPerfil,
} from 'store/modules/auth/IAuthUserPerfil';
import styles from './relatory.module.scss';

interface Params {
  id: string;
}

export default function Relatory() {
  const [refetchInterval, setRefetchInterval] = useState<boolean>(false);
  const [tempStatus, setTempStatus] = useState<ReportStatus | string>();

  const history = useHistory();
  const params = useParams<Params>();
  const { showToast } = useContext(LayoutContext);

  const { user } = useContext(AuthContext);

  const queryClient = useQueryClient();

  const parsedToken: JWTAssessorJson | null = getParsedJWTAssessorAccessToken(
    user!.access_token
  );
  const perfil: IAuthUserPerfil | null = parsedToken
    ? getProfileByJWTRoles(parsedToken.roles)
    : null;

  const { data: pageInfoData } = useGetPageReportInfo(params.id);
  const { data: reportData, isLoading } = useGetReports(params.id, {
    refetchInterval: refetchInterval ? 30000 : false,
  });
  const { mutateAsync } = useGenerateReport();

  const hasGeneratingStatus = useCallback((reports: ReportTypes[]) => {
    return reports?.some(
      (report) =>
        report.reportData?.status === ReportStatus.GENERATING ||
        report.reportData?.status === ReportStatus.NOT_AVAILABLE
    );
  }, []);

  const breadcrumb = useMemo<IBreadCrumbItem[]>(
    () => [
      { nome: translation.MainRoute.title, url: AssessorRoutePath },
      { nome: translation.Assessor.Evento.Relatory.title },
    ],
    []
  );

  const startDateInText = useMemo(() => {
    if (!pageInfoData?.startDate) return '-';
    const startDate = parseISO(pageInfoData.startDate);

    return `${translation.AssembleiaDateTime.day(
      startDate.getDate(),
      translation.AssembleiaDateTime[
        `month-${startDate.getMonth()}` as keyof typeof translation.AssembleiaDateTime
      ],
      startDate.getFullYear()
    )} ${moment(startDate).format(DateFormats.Time)}`;
  }, [pageInfoData]);

  const reportTitle = useCallback((type: ReportType) => {
    switch (type) {
      case ReportType.CONVOCATION:
        return 'Relatório de convocados';
      case ReportType.EVENT:
        return 'Relatório de evento';
      case ReportType.EVENT_SURVEY:
        return 'Relatório pesquisa de satisfação';
      case ReportType.PRESENCE:
        return 'Relatório de presença';
      case ReportType.PRESENCE_PDF:
        return 'Relatório de presença PDF';
      case ReportType.VOTING:
        return 'Relatório de votos digitais';
      case ReportType.QUORUM:
        return 'Relatório de quórum';
      case ReportType.MESSENGER:
        return 'Relatório de mensagens';
      case ReportType.GUEST:
        return 'Relatório de convidados';
      default:
        return 'Relatório não mapeado';
    }
  }, []);

  // Downloads

  const generateUrl = (endpoint: string) => {
    return `${getAssembleiasAssessorRelatoriosEndpoint('1')}${endpoint}`;
  };

  const {
    refetch: refetchSummonedReport,
    isError: summonedIsError,
    error: summonedError,
  } = useReportDownload({
    url: generateUrl(`evento/convocacao/${params.id}`),
    id: params.id,
    fileNamePrefix: 'Relatorio_Convocado',
    queryKey: Queries.GetSummonedReport,
    extra: {
      enabled: false,
    },
  });

  const {
    refetch: refetchAttendanceReport,
    isError: attendanceIsError,
    error: attendanceError,
  } = useReportDownload({
    url: generateUrl(`presence-report/${params.id}`),
    id: params.id,
    fileNamePrefix: 'Relatorio_Presentes',
    queryKey: Queries.GetAttendanceReport,
    extra: {
      enabled: false,
    },
  });

  const {
    refetch: refetchAttendancePdfReport,
    isError: attendancePdfIsError,
    error: attendancePdfError,
  } = useReportDownload({
    url: generateUrl(`presence-pdf-report/${params.id}`),
    id: params.id,
    fileNamePrefix: 'Relatorio_Presentes_PDF',
    queryKey: Queries.GetAttendancePdfReport,
    extra: {
      enabled: false,
    },
  });

  const {
    refetch: refetchQuorumReport,
    isError: quorumIsError,
    error: quorumError,
  } = useReportDownload({
    url: generateUrl(`evento/quorum/${params.id}`),
    id: params.id,
    fileNamePrefix: 'Relatorio_Quórum',
    queryKey: Queries.GetReportQuorum,
    method: 'GET',
    extra: {
      enabled: false,
    },
  });

  const {
    refetch: refetchMessageReport,
    isError: messageIsError,
    error: messageError,
  } = useReportDownload({
    url: generateUrl(`evento/duvidas/${params.id}`),
    id: params.id,
    fileNamePrefix: 'Relatorio_de_Mensagens',
    queryKey: Queries.GetReportMessage,
    extra: {
      enabled: false,
    },
  });

  const {
    refetch: refetchVotingReport,
    isError: votingIsError,
    error: votingError,
  } = useReportDownload({
    url: generateUrl(`voting-report/${params.id}`),
    id: params.id,
    fileNamePrefix: 'Relatorio_de_Votos_Digitais',
    queryKey: Queries.GetReportVoting,
    extra: {
      enabled: false,
    },
  });

  const {
    refetch: refetchEventReport,
    isError: eventIsError,
    error: eventError,
  } = useReportDownload({
    url: generateUrl(`event-report/${params.id}`),
    id: params.id,
    fileNamePrefix: 'Relatorio_do_Evento',
    queryKey: Queries.GetReportEvent,
    extra: {
      enabled: false,
    },
  });

  const {
    refetch: refetchGuestReport,
    isError: guestIsError,
    error: guestError,
  } = useReportDownload({
    url: generateUrl(`guest-report/${params.id}`),
    id: params.id,
    fileNamePrefix: 'Relatorio_de_Convidados',
    queryKey: Queries.GetReportGuest,
    extra: {
      enabled: false,
    },
  });

  const {
    refetch: refetchSurveyReport,
    isError: surveyIsError,
    error: surveyError,
  } = useReportDownload({
    url: generateUrl(`evento/pesquisa/resultado?eventId=${params.id}`),
    id: params.id,
    fileNamePrefix: 'Relatorio_da_Pesquisa_de_Satisfação',
    queryKey: Queries.GetReportSurvey,
    extra: {
      enabled: false,
    },
  });

  const handleDownloadResponse = useCallback(
    ({ response, isError, error }: HandleDownloadResponseParams) => {
      if (isError || (response as any).isError) {
        showToast({
          pauseOnFocusLoss: true,
          theme: ToastTypes.Error,
          showStatusBar: true,
          timeout: 4000,
          label:
            translation.Toasts.Errors.Keys[
              (`${
                ((response! as any).error.message !==
                  'Request failed with status code 417' &&
                  (response! as any).error.message) ||
                error!.response?.data.message ||
                error!.response?.data ||
                error!.message
              }` as keyof typeof translation.Toasts.Errors.Keys) ??
                translation.Toasts.Errors.defaultAlternative
            ],
        });
      } else if (response?.data) {
        DownloadTrigger(response.data.fileName, response.data.data);
      }
    },
    [showToast]
  );

  const reportMap: Partial<Record<ReportType, any>> = {
    [ReportType.CONVOCATION]: {
      refetch: refetchSummonedReport,
      isError: summonedIsError,
      error: summonedError,
    },
    [ReportType.PRESENCE]: {
      refetch: refetchAttendanceReport,
      isError: attendanceIsError,
      error: attendanceError,
    },
    [ReportType.PRESENCE_PDF]: {
      refetch: refetchAttendancePdfReport,
      isError: attendancePdfIsError,
      error: attendancePdfError,
    },
    [ReportType.QUORUM]: {
      refetch: refetchQuorumReport,
      isError: quorumIsError,
      error: quorumError,
    },
    [ReportType.MESSENGER]: {
      refetch: refetchMessageReport,
      isError: messageIsError,
      error: messageError,
    },
    [ReportType.VOTING]: {
      refetch: refetchVotingReport,
      isError: votingIsError,
      error: votingError,
    },
    [ReportType.EVENT]: {
      refetch: refetchEventReport,
      isError: eventIsError,
      error: eventError,
    },
    [ReportType.GUEST]: {
      refetch: refetchGuestReport,
      isError: guestIsError,
      error: guestError,
    },
    [ReportType.EVENT_SURVEY]: {
      refetch: refetchSurveyReport,
      isError: surveyIsError,
      error: surveyError,
    },
  };

  const downloadReport = useCallback(
    async (reportType: ReportType): Promise<void> => {
      const report = reportMap[reportType];
      if (report) {
        const response = await report.refetch();

        handleDownloadResponse({
          response: response as { data: any },
          isError: report.isError,
          error: report.error as AxiosError<Error>,
        });
      } else {
        showToast({
          theme: ToastTypes.Error,
          pauseOnFocusLoss: true,
          showStatusBar: true,
          timeout: 4000,
          label: `O relatório do tipo ${reportType} ainda não está disponível.`,
        });
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [handleDownloadResponse, showToast]
  );

  const isReportDisabled = useCallback(
    (report: ReportTypes, status: SituationValues) => {
      if (status) {
        const conditions = [
          {
            condition:
              report.reportType === ReportType.PRESENCE &&
              (![
                SituationValues.BLOCKED,
                SituationValues.IN_PREPARATION,
                SituationValues.IN_ASSEMBLY,
                SituationValues.FINISHED,
              ].includes(status) ||
                pageInfoData?.modality === AssemblyModalities.OFFLINE),
          },
          {
            condition:
              report.reportType === ReportType.PRESENCE_PDF &&
              status !== SituationValues.FINISHED,
          },
          {
            condition:
              report.reportType === ReportType.QUORUM &&
              (![
                SituationValues.IN_ASSEMBLY,
                SituationValues.FINISHED,
              ].includes(status) ||
                pageInfoData?.modality === AssemblyModalities.OFFLINE),
          },
          {
            condition:
              report.reportType === ReportType.MESSENGER &&
              (![
                SituationValues.IN_PREPARATION,
                SituationValues.IN_ASSEMBLY,
                SituationValues.FINISHED,
              ].includes(status) ||
                pageInfoData?.modality === AssemblyModalities.OFFLINE ||
                ([AuthUserPerfilId.PertencerConsulta].includes(perfil!?.id!) &&
                  pageInfoData?.typeAcronym !== 'REU')),
          },
          {
            condition:
              report.reportType === ReportType.VOTING &&
              pageInfoData?.modality === AssemblyModalities.OFFLINE
                ? true
                : report.reportType === ReportType.VOTING &&
                  ([AuthUserPerfilId.PertencerConsulta].includes(
                    perfil!?.id!
                  ) &&
                  [
                    SituationValues.FINISHED,
                    SituationValues.IN_ASSEMBLY,
                  ].includes(status) &&
                  pageInfoData?.typeAcronym === 'REU'
                    ? false
                    : ![
                        SituationValues.IN_ASSEMBLY,
                        SituationValues.FINISHED,
                      ].includes(status)
                    ? true
                    : ![
                        AuthUserPerfilId.PertencerAdmin,
                        AuthUserPerfilId.PertencerGestao,
                      ].includes(perfil!?.id!)),
          },
          {
            condition:
              report.reportType === ReportType.EVENT &&
              (![SituationValues.FINISHED].includes(status) ||
                ([AuthUserPerfilId.PertencerConsulta].includes(perfil!?.id!) &&
                  pageInfoData?.typeAcronym !== 'REU')),
          },
          {
            condition:
              report.reportType === ReportType.GUEST &&
              (pageInfoData?.modality === AssemblyModalities.OFFLINE ||
                ![
                  SituationValues.IN_ASSEMBLY,
                  SituationValues.FINISHED,
                ].includes(status)),
          },
          {
            condition:
              report.reportType === ReportType.EVENT_SURVEY &&
              (![SituationValues.FINISHED].includes(status) ||
                ([AuthUserPerfilId.PertencerConsulta].includes(perfil!?.id!) &&
                  pageInfoData?.typeAcronym !== 'REU')),
          },
        ];

        return conditions.some((cond) => cond.condition);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [pageInfoData]
  );

  const validateError = useCallback(
    (reportType: ReportType, customError: boolean) => {
      const toast = (err: AxiosError<Error>) => {
        return showToast({
          pauseOnFocusLoss: true,
          theme: ToastTypes.Error,
          showStatusBar: true,
          timeout: 4000,
          label:
            translation.Toasts.Errors.Keys[
              `${
                (err as AxiosError<Error>).response?.data.message ||
                (err as AxiosError<Error>).response?.data ||
                (err as AxiosError<Error>).message
              }` as keyof typeof translation.Toasts.Errors.Keys
            ] ?? translation.Toasts.Errors.defaultAlternative,
        });
      };

      return mutateAsync(
        {
          eventId: Number(params.id),
          reportType: reportType,
        },
        {
          onSuccess: (
            _,
            variables: {
              eventId: number;
              reportType: ReportType;
            }
          ) => {
            setTempStatus(variables.reportType);
            setRefetchInterval(true);
          },
          onError: (error) => {
            if (customError) {
              if (
                ((error as AxiosError<Error>).response?.data.message ||
                  (error as AxiosError<Error>).response?.data ||
                  error.message) === 'report.notAllowed'
              ) {
                showToast({
                  pauseOnFocusLoss: true,
                  theme: ToastTypes.Warning,
                  showStatusBar: true,
                  timeout: 6000,
                  label:
                    'O relatório está sendo gerado por outra pessoa no momento. Por favor, aguarde um pouco. A tela será atualizada assim que o relatório estiver disponível.',
                });
                setRefetchInterval(true);
              } else {
                toast(error as AxiosError<Error>);
              }
            } else {
              toast(error as AxiosError<Error>);
            }
          },
        }
      );
    },
    [mutateAsync, params.id, showToast]
  );

  useEffect(() => {
    setRefetchInterval(hasGeneratingStatus(reportData!));
  }, [reportData, hasGeneratingStatus]);

  useEffect(() => {
    if (!refetchInterval) {
      setTempStatus('');
    }
  }, [refetchInterval]);

  useEffect(() => {
    if (
      [
        SituationValues.EDITABLE,
        SituationValues.EXPIRED,
        SituationValues.RESERVABLE,
      ].includes(pageInfoData?.status!) ||
      [AuthUserPerfilId.PertencerChat].includes(perfil!?.id!)
    ) {
      if ([AuthUserPerfilId.PertencerChat].includes(perfil!?.id!)) {
        showToast({
          pauseOnFocusLoss: true,
          theme: ToastTypes.Warning,
          showStatusBar: true,
          timeout: 5500,
          label: 'Este perfil não possui acesso à tela de relatórios',
        });
        history.push(AssessorRoutePath);
      } else {
        showToast({
          pauseOnFocusLoss: true,
          theme: ToastTypes.Warning,
          showStatusBar: true,
          timeout: 5500,
          label:
            'Esta URL não está disponível para eventos nas seguintes situações: Em edição, Expirado e Reservado.',
        });
        history.push(AssessorRoutePath);
      }
    }
  }, [history, pageInfoData?.status, perfil, showToast]);

  useEffect(() => {
    queryClient.invalidateQueries({
      queryKey: [Queries.GetReportInfo],
    });
    queryClient.invalidateQueries({
      queryKey: [Queries.GetReports],
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Suspense fallback={<TopLoader opened />}>
      <PrivateContainer
        header={
          <Fragment>
            <Col>
              <Title
                showBackButton
                onBackClick={() => history.push(AssessorRoutePath)}
              >
                {translation.Assessor.Evento.Relatory.title}
              </Title>
            </Col>
            <Col>
              <Breadcrumb itens={breadcrumb} />
            </Col>
            <Row
              className={styles['big-title']}
              style={{ marginBottom: '20px' }}
            >
              <Col>
                <h2>{pageInfoData?.title ?? '-'}</h2>
              </Col>
              <Col>
                <p>
                  {pageInfoData?.type} - {pageInfoData?.city} -{' '}
                  {startDateInText}
                  {pageInfoData?.modality === AssemblyModalities.ONLINE ? (
                    <Fragment />
                  ) : (
                    <Fragment>
                      <br />
                      {translation.Assessor.Evento.Vote.address(
                        pageInfoData?.address!
                      )}
                    </Fragment>
                  )}
                </p>
              </Col>
            </Row>
            <Col>
              <hr
                style={{
                  width: '100%',
                  height: '1px',
                  background: '#cdd3cd',
                  border: 'none',
                }}
              />
            </Col>
            <Row className={styles['big-title']}>
              <Collapse opened={!isLoading} animateOpacity>
                <Col>
                  <h2>Lista de relatórios desse evento</h2>
                </Col>
                <Col>
                  <p>Escolha um relatório do seu evento, gere e baixe-os</p>
                </Col>
              </Collapse>
            </Row>
          </Fragment>
        }
        className={styles.root}
      >
        <Row>
          <Collapse opened={!isLoading} animateOpacity>
            <Row>
              {reportData
                ?.filter(
                  (report) => !isReportDisabled(report, pageInfoData?.status!)
                )
                .map((report) => {
                  return (
                    <Col key={report.reportType} cols={[12, 12, 6, 4, 3]}>
                      <RelatoryCard
                        w100
                        title={reportTitle(report.reportType)}
                        status={
                          tempStatus === report.reportType
                            ? ReportStatus.GENERATING
                            : report.reportData?.status
                        }
                        ldap={report.reportData?.lastLdap}
                        date={report.reportData?.lastDate}
                        disabled={isReportDisabled(
                          report,
                          pageInfoData?.status!
                        )}
                        refetchClick={
                          report.allowGenerate
                            ? () => validateError(report.reportType, false)
                            : undefined
                        }
                        reportData={report.reportData}
                        buttonClick={() =>
                          report.allowGenerate &&
                          isNullOrUndefined(report.reportData)
                            ? validateError(report.reportType, true)
                            : downloadReport(report.reportType)
                        }
                        nextDate={
                          report.reportData?.status === ReportStatus.READY &&
                          pageInfoData?.status !== SituationValues.FINISHED &&
                          pageInfoData?.modality !== AssemblyModalities.OFFLINE
                            ? report.reportData?.nextDate
                            : report.reportData?.status ===
                                ReportStatus.READY &&
                              report.reportType === ReportType.EVENT_SURVEY &&
                              pageInfoData?.status === SituationValues.FINISHED
                            ? report.reportData?.nextDate
                            : undefined
                        }
                        type={report.reportType}
                      />
                    </Col>
                  );
                })}
            </Row>
          </Collapse>
        </Row>
      </PrivateContainer>
    </Suspense>
  );
}
